// src/services/buyerPhoneNumberService.ts

import { getAccessToken } from "@lib/shared/cookies";
import HTTP, { apiHandler } from "@services/http.service";
import { AxiosRequestConfig } from "axios";
export type BuyerPhoneNumberByZipcodeResponse = {
    data: {
        phoneNumber: string;
    };
    error: BuyerPhoneNumberByZipcodeErrorResponse | null;
    success: boolean;
};

export type BuyerPhoneNumberByZipcodeRequest = {
    zipCode: string;
    categoryId: number;
};

export type BuyerPhoneNumberByZipcodeErrorResponse = {
    success: boolean;
    status: number;
    code: string;
    message: string;
    src: string;
    data: null;
};

export type BuyerZipcodePhoneNumberData = {
    phoneNumber: string;
};

export type BuyerZipcodePhoneNumberOptions = {
    zipCode: string;
    categoryId?: number;
    onSuccess?: (phoneNumber: string) => void;
    onError?: (error: string) => void;
};
export type ApiCallFields = "zipcode";

export class BuyerPhoneNumberService {
    static async handleApiCall<T>(
        apiCallField: ApiCallFields | undefined,
        data: T,
    ) {
        switch (apiCallField?.toLowerCase()) {
            case "zipcode": {
                await this.handleZipcodeApiCall(
                    data as BuyerZipcodePhoneNumberOptions,
                );
                break;
            }
        }
    }

    /**
     * Fetches buyer phone number based on zipcode and category
     */
    static async fetchPhoneNumberByZipcode(
        zipCode: string,
        categoryId?: number,
    ): Promise<string | null> {
        const accessToken = getAccessToken();

        const config: AxiosRequestConfig = {
            method: "post",
            url: `/api/phoneNumberByZipcode`,
            data: {
                zipCode,
                categoryId,
            },
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken as string}`,
            },
        };

        const { data } = await apiHandler<BuyerPhoneNumberByZipcodeResponse>(
            () => HTTP.client(config),
        );

        return data?.data?.phoneNumber ?? null;
    }

    /**
     * Handles the complete zipcode API call flow with callbacks
     */
    static async handleZipcodeApiCall(
        options: BuyerZipcodePhoneNumberOptions,
    ): Promise<BuyerZipcodePhoneNumberData> {
        try {
            const phoneNumber = await this.fetchPhoneNumberByZipcode(
                options.zipCode,
                options.categoryId,
            );

            if (phoneNumber) {
                options.onSuccess?.(phoneNumber);
                return {
                    phoneNumber,
                };
            } else {
                const errorMessage = "Something went wrong, please try again";
                options.onError?.(errorMessage);
                return {
                    phoneNumber: "",
                };
            }
        } catch (error) {
            const errorMessage = "Something went wrong, please try again";
            options.onError?.(errorMessage);
            return {
                phoneNumber: "",
            };
        }
    }
}
